/* eslint-disable react-refresh/only-export-components -- hook + provider is standard React context pattern */
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "@/packages/utils";
import { AudineroIcon } from "./AudineroIcon";
import { AnimatePresence, motion } from "framer-motion";
import type { ChatPanelContextType } from "./types";

const ChatPanelContext = createContext<ChatPanelContextType | null>(null);

function FAB({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation(["common"]);
  return (
    <div className="fixed bottom-6 right-6 z-40">
      {/* Ambient pulse ring */}
      <motion.div
        className="absolute inset-0 rounded-2xl bg-accent/15"
        animate={{ scale: [1, 1.5, 1], opacity: [0.4, 0, 0.4] }}
        transition={{ duration: 3, ease: "easeInOut", repeat: Infinity }}
      />
      <motion.button
        onClick={onClick}
        type="button"
        className="relative h-12 w-12 rounded-2xl bg-gradient-to-br from-accent/90 to-accent text-white shadow-lg shadow-accent/20 hover:shadow-xl hover:shadow-accent/30 hover:scale-105 transition-all duration-200 flex items-center justify-center cursor-pointer"
        aria-label={t("ui.chatPanel.openAssistant")}
        initial={{ scale: 0, rotate: -10 }}
        animate={{ scale: 1, rotate: 0 }}
        exit={{ scale: 0, opacity: 0, rotate: 10 }}
        transition={{ type: "spring", damping: 20, stiffness: 300 }}
      >
        <AudineroIcon size={22} />
      </motion.button>
    </div>
  );
}

export function ChatPanelProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const openPanel = useCallback(() => {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    setIsOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    requestAnimationFrame(() => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    });
  }, []);

  const togglePanel = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) {
        previousFocusRef.current = document.activeElement as HTMLElement | null;
      } else {
        requestAnimationFrame(() => {
          previousFocusRef.current?.focus();
          previousFocusRef.current = null;
        });
      }
      return !prev;
    });
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "j") {
        e.preventDefault();
        togglePanel();
      }
      if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        closePanel();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [togglePanel, closePanel, isOpen]);

  return (
    <ChatPanelContext.Provider
      value={{ isOpen, openPanel, closePanel, togglePanel }}
    >
      {children}
      <AnimatePresence>
        {!isOpen && <FAB onClick={openPanel} />}
      </AnimatePresence>
    </ChatPanelContext.Provider>
  );
}

export function useChatPanel(): ChatPanelContextType {
  const context = use(ChatPanelContext);
  if (!context) {
    throw new Error("useChatPanel must be used within a ChatPanelProvider");
  }
  return context;
}
